export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://twinkletoes.sch.ng').replace(/\/$/, '');
export const SITE_NAME = 'Twinkle Toes Schools';

export const SEO_PAGES = {
  '/': {
    title: 'Twinkle Toes Schools | Nursery & Primary School in Orozo, Abuja',
    description: 'Discover Twinkle Toes Schools in Cajaah Estate, Orozo, Abuja. Explore our crèche, nursery and primary classes, school life and admissions.',
    label: 'Home',
  },
  '/about': {
    title: 'About Our School in Orozo, Abuja | Twinkle Toes Schools',
    description: 'Meet Twinkle Toes Schools in Orozo, Abuja. Learn about our values, teaching approach, classrooms, library and playground for growing learners.',
    label: 'About',
  },
  '/admissions': {
    title: 'Crèche, Nursery & Primary Admissions | Twinkle Toes Schools',
    description: 'Apply to Twinkle Toes Schools in Orozo, Abuja. Explore crèche, nursery and primary admissions, arrange a school visit and send an enquiry.',
    label: 'Admissions',
  },
  '/gallery': {
    title: 'School Photo Gallery | Twinkle Toes Schools, Orozo, Abuja',
    description: 'See school life at Twinkle Toes Schools in Orozo, Abuja. Browse photos of classroom learning, sports, school events and campus activities.',
    label: 'Gallery',
    type: 'CollectionPage',
  },
  '/news': {
    title: 'School News & Events | Twinkle Toes Schools, Abuja',
    description: 'Read the latest news, events and announcements from Twinkle Toes Schools in Orozo, Abuja. Stay connected with our school community.',
    label: 'News & Events',
    type: 'CollectionPage',
  },
  '/contact': {
    title: 'Contact & Visit Our Orozo School | Twinkle Toes Schools',
    description: 'Visit Twinkle Toes Schools in Cajaah Estate, Orozo, Abuja. Find our location, opening hours and contact details, or send the school an enquiry.',
    label: 'Contact',
    type: 'ContactPage',
  },
};

export function normalizePath(pathname) {
  return pathname.split(/[?#]/)[0].replace(/\/+$/, '').toLowerCase() || '/';
}

export function absoluteUrl(path) {
  try {
    const url = new URL(path, `${SITE_URL}/`);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : `${SITE_URL}/logo.jpg`;
  } catch {
    return `${SITE_URL}/logo.jpg`;
  }
}

function isoDate(value) {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function excerpt(post) {
  return (post.excerpt || post.body || '')
    .replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160)
    || `Read ${post.title} from ${SITE_NAME} in Orozo, Abuja.`;
}

export function getPageSeo(pathname, post) {
  const path = normalizePath(pathname);
  const page = SEO_PAGES[path];
  const article = /^\/news\/\d+$/.test(path) && post && Number(post.published) === 1;
  const isAdmin = path === '/admin' || path.startsWith('/admin/');
  const indexable = Boolean(page || article) && process.env.REACT_APP_NOINDEX !== 'true';
  const title = article ? `${post.title} | ${SITE_NAME}` : page?.title
    || `${isAdmin ? 'Admin Portal' : 'Page Not Found'} | ${SITE_NAME}`;
  const description = article ? excerpt(post) : page?.description
    || (isAdmin ? 'Twinkle Toes Schools administration portal.' : 'The requested page could not be found. Explore Twinkle Toes Schools in Orozo, Abuja.');
  const canonical = `${SITE_URL}${path === '/' ? '/' : path}`;
  const image = absoluteUrl(article && post.image_url ? post.image_url : '/logo.jpg');
  const schoolId = `${SITE_URL}/#school`;
  const websiteId = `${SITE_URL}/#website`;
  const graph = [
    {
      '@type': 'School', '@id': schoolId, name: SITE_NAME, url: `${SITE_URL}/`,
      logo: `${SITE_URL}/logo.jpg`, image: `${SITE_URL}/logo.jpg`,
      description: SEO_PAGES['/'].description,
      address: {
        '@type': 'PostalAddress', streetAddress: 'Cajaah Estate, Orozo',
        addressLocality: 'Abuja', addressRegion: 'Federal Capital Territory', addressCountry: 'NG',
      },
    },
    { '@type': 'WebSite', '@id': websiteId, name: SITE_NAME, url: `${SITE_URL}/`, publisher: { '@id': schoolId }, inLanguage: 'en-NG' },
    {
      '@type': page?.type || 'WebPage', '@id': `${canonical}#webpage`, url: canonical,
      name: title, description, isPartOf: { '@id': websiteId }, about: { '@id': schoolId }, inLanguage: 'en-NG',
      ...(path !== '/' ? { breadcrumb: { '@id': `${canonical}#breadcrumb` } } : {}),
    },
  ];
  if (path !== '/') {
    const crumbs = [{ name: 'Home', item: `${SITE_URL}/` }];
    if (article) crumbs.push({ name: 'News & Events', item: `${SITE_URL}/news` });
    crumbs.push({ name: article ? post.title : page?.label || 'Page', item: canonical });
    graph.push({
      '@type': 'BreadcrumbList', '@id': `${canonical}#breadcrumb`,
      itemListElement: crumbs.map((item, i) => ({ '@type': 'ListItem', position: i + 1, ...item })),
    });
  }
  if (article) {
    graph.push({
      '@type': 'NewsArticle', '@id': `${canonical}#article`, headline: post.title,
      description, image: [image], mainEntityOfPage: { '@id': `${canonical}#webpage` },
      datePublished: isoDate(post.created_at), dateModified: isoDate(post.updated_at || post.created_at),
      author: post.author && post.author !== SITE_NAME
        ? { '@type': 'Person', name: post.author }
        : { '@type': 'Organization', name: SITE_NAME, url: `${SITE_URL}/` },
      publisher: { '@id': schoolId }, inLanguage: 'en-NG',
    });
  }
  return {
    title, description, canonical, image, article: Boolean(article),
    robots: indexable ? 'index, follow, max-image-preview:large' : 'noindex, follow',
    structuredData: indexable ? { '@context': 'https://schema.org', '@graph': graph } : null,
  };
}

export function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[character]));
}

export function serializeJson(value) {
  return JSON.stringify(value).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026');
}

// The same head markup is used at build time and after client-side navigation.
export function renderSeoHead(seo) {
  const meta = (key, value, property = false) => `<meta data-seo ${property ? 'property' : 'name'}="${key}" content="${escapeHtml(value)}">`;
  return [
    `<title data-seo>${escapeHtml(seo.title)}</title>`,
    meta('description', seo.description), meta('robots', seo.robots),
    `<link data-seo rel="canonical" href="${escapeHtml(seo.canonical)}">`,
    meta('og:site_name', SITE_NAME, true), meta('og:locale', 'en_NG', true),
    meta('og:type', seo.article ? 'article' : 'website', true),
    meta('og:title', seo.title, true), meta('og:description', seo.description, true),
    meta('og:url', seo.canonical, true), meta('og:image', seo.image, true),
    meta('og:image:alt', seo.article ? seo.title : `${SITE_NAME} logo`, true),
    meta('twitter:card', seo.article ? 'summary_large_image' : 'summary'),
    meta('twitter:title', seo.title), meta('twitter:description', seo.description),
    meta('twitter:image', seo.image), meta('twitter:image:alt', seo.article ? seo.title : `${SITE_NAME} logo`),
    seo.structuredData ? `<script data-seo type="application/ld+json">${serializeJson(seo.structuredData)}</script>` : '',
  ].join('\n');
}
